import { registerRegisterDetail } from '@/features/startTask/preset_task/shared/preset/register/registerDetailRegistry';

function trim(value: unknown): string {
  return value === null || value === undefined ? '' : String(value).trim();
}

function buildLocalAddress(parts: {
  blockNo?: unknown;
  streetName?: unknown;
  buildingName?: unknown;
  level?: unknown;
  unit?: unknown;
  postalCode?: unknown;
}): string {
  const blockNo = trim(parts.blockNo);
  const streetName = trim(parts.streetName);
  const buildingName = trim(parts.buildingName);
  const level = trim(parts.level);
  const unit = trim(parts.unit);
  const postalCode = trim(parts.postalCode);
  const line1 = [blockNo, streetName].filter(Boolean).join(' ').trim();
  const line2 = level || unit ? `#${[level, unit].filter(Boolean).join('-')}` : '';
  return [line1, line2, buildingName, postalCode ? `SINGAPORE ${postalCode}` : ''].filter(Boolean).join(', ');
}

function buildForeignAddress(line1: unknown, line2: unknown): string {
  return [trim(line1), trim(line2)].filter(Boolean).join(', ');
}

function formatResidentialAddress(raw: any): string {
  if (typeof raw === 'string') return trim(raw);
  const contact = raw && typeof raw === 'object' ? raw : {};
  const local = buildLocalAddress({
    blockNo: contact.blockNo,
    streetName: contact.streetName,
    buildingName: contact.buildingName,
    level: contact.addrLevel,
    unit: contact.addrUnit,
    postalCode: contact.postalCode,
  });
  return local || buildForeignAddress(contact.foreignAddressLine1, contact.foreignAddressLine2);
}

function formatServiceAddress(raw: any): string {
  if (typeof raw === 'string') return trim(raw);
  const contact = raw && typeof raw === 'object' ? raw : {};
  const local = buildLocalAddress({
    blockNo: contact.altBlockNo,
    streetName: contact.altStreetName,
    buildingName: contact.altBuildingName,
    level: contact.altAddrLevel,
    unit: contact.altAddrUnit,
    postalCode: contact.altPostalCode,
  });
  return local || buildForeignAddress(contact.altForeignAddressLine1, contact.altForeignAddressLine2);
}

function structuredPrimaryMatchesAlternate(contact: Record<string, any>): boolean {
  return (
    trim(contact.blockNo) === trim(contact.altBlockNo) &&
    trim(contact.streetName) === trim(contact.altStreetName) &&
    trim(contact.postalCode) === trim(contact.altPostalCode) &&
    trim(contact.addrLevel) === trim(contact.altAddrLevel) &&
    trim(contact.addrUnit) === trim(contact.altAddrUnit) &&
    trim(contact.buildingName) === trim(contact.altBuildingName)
  );
}

/** 레지스터 상세 — 한 줄 주소(primaryAddress 등) 우선, 거주와 동일 구조면 resignDirectorAddress 와 맞춤 */
function formatContactAddressForRegister(raw: any, source?: Record<string, any>): string {
  if (typeof raw === 'string') return trim(raw);
  const contact = raw && typeof raw === 'object' ? raw : {};
  const oneLine = trim(contact.primaryAddress || contact.serviceAddress);
  if (oneLine) return oneLine;
  const resForm = trim(source?.resignDirectorAddress);
  if (resForm && structuredPrimaryMatchesAlternate(contact)) {
    return resForm;
  }
  return formatServiceAddress(contact);
}

/** B32102 / B32004 — Director cessation register summary */
const withdrawDirectorDetailMap = {
  title: 'Withdraw Director Details',
  fields: [
    {
      label: 'Full Name',
      key: ['resignDirectorName', 'officerName', 'contactData.contactName'],
    },
    {
      label: 'NRIC / FIN / Passport No',
      key: ['directorIdentificationNo', 'contactData.contactRegNo'],
    },
    {
      label: 'Date of Birth',
      key: ['directorDateOfBirth', 'contactData.dateOfBirth'],
    },
    {
      label: 'Nationality',
      key: ['directorNationality', 'contactData.nationalityText'],
    },
    { label: 'Email', key: ['directorEmail', 'contactData.contactEmail'] },
    { label: 'Contact No', key: ['directorContactNo', 'contactData.mobileNo'] },
    {
      label: 'Date of Appointment',
      key: [
        'appointmentEffectiveDate',
        'appointmentDate',
        'appointedDate',
        'dateOfAppointed',
        'contactData.dateOfAppointment',
        'eFilingDataValidation.CessationList.0.ceasedPositionHolder.ceasedPositionHeld.0.entryDate',
        'eFilingDataForApi.CessationList.0.ceasedPositionHolder.ceasedPositionHeld.0.entryDate',
      ],
    },
    {
      label: 'Date of Cessation',
      key: ['dateOfCessation', 'cessationDate', 'resignationDate', 'resignation_date'],
    },
    {
      label: 'Residential Address',
      key: ['resignDirectorAddress', 'contactData'],
      format: formatResidentialAddress,
    },
    {
      label: 'Contact Address',
      key: ['directorAlternateAddress', 'contactData.primaryAddress', 'contactData'],
      format: formatContactAddressForRegister,
    },
  ],
};

registerRegisterDetail('B32102', withdrawDirectorDetailMap);
registerRegisterDetail('B32004', withdrawDirectorDetailMap);
